import { SelectionModel } from '@angular/cdk/collections';
import { Component, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { OrderDirection, PagedCollectionQueryParams, PagedCollectionQueryResult, ViewSettingsRepositoryBase } from '@skylab-shared/business';
import { ViewSettingsRepository } from '@skylab-angular-shared/business';
import { DataFormats } from '@skylab-shared/constants';
import { ConsoleLogger, DeleteResponse, L10nBase, LocalContext, RepositoryResponseCode, RootContextBase } from '@skylab-shared/core';
import { EnumValue } from '@skylab-shared/dataModel';
import { NavigationRoute } from '@skylab-shared/scenes';
import { ToastDelay } from '@skylab-shared/ui';
import { ListenersRegistry, SerializationUtils } from '@skylab-shared/utils';

import { Platform } from '@angular/cdk/platform';
import { NavigationExtras } from '@angular/router';
import { RoutingService } from '@skylab-angular-shared/core';
import { DialogInsService, DialogState, FilterDataDialogComponentData, GridResizedEvent, GridSortingEvent, Menu, MenuItem, MenuItemSeparator, MenuService, OrderingDirection, SearchScope, ServiceGridColumn, ServiceGridOptions, SortDataDialogComponentData, ToastService } from '@skylab-angular-shared/ui';
import { Dictionary } from '@skylab-shared/types';
import { RootContainerOverlayService } from '../common/root-container-overlay.service';
import { ServiceFiltersConfig } from './filters-options.service-base';
import { ServiceViewCache } from './view-cache.service';

type SelectionEntityMode = 'first-row' | 'sibling-row' | 'clear' | 'same-indexes';

//#region INTERFACES
export interface ServiceStates {
    new?: string;
    edit?: string;
    replicate?: string;
    service: string;
}

export interface ServiceKindItem {
    name: string;
    viewKind?: EnumValue;
    default?: boolean;
    searchServiceName?: string | null;
}

export interface ServiceMenuItems {
    add: MenuItem;
    edit: MenuItem;
    separator: MenuItemSeparator;
    duplicate: MenuItem;
    remove: MenuItem;
}

export interface ServiceViewModel<TCollectionItem, TFilterParams, TGridColumnIdentity> {
    search: string;
    searchActiveTriggered(val: boolean): void;
    searchActionAsync(): Promise<void>;
    searchActive: boolean;
    searchScope: SearchScope;
    isFiltered: () => boolean;
    onEdit(entity: TCollectionItem): void;
    onReplicate(entity: TCollectionItem): void;
    onAdd(kind: string): void;
    getAddRouteObject: () => NavigationRoute;
    getEditRouteObject: (entity?: TCollectionItem) => NavigationRoute;
    kind: ServiceKindItem;

    // grid
    gridOptions: ServiceGridOptions<TCollectionItem, TGridColumnIdentity, any>;
    gridColumns$: BehaviorSubject<ServiceGridColumn<TGridColumnIdentity, any, TCollectionItem, any, any>[]>;
    gridDataSource$: BehaviorSubject<TCollectionItem[]>;
    getSelectedEntitiesCount: () => number;

    //pagination
    paginationOptions: { limit: number, range: number };
    currentPage: number;
    totalAmount: number;
    getPageUrl(page: number): string;
    filterParams: TFilterParams;
    filterChipsOptions: ServiceFiltersConfig<TFilterParams>;
}
//#endregion

@Component({
    template: '',
})
export abstract class ServiceBase<
    TFilterParams,
    TCollectionItem extends { id: number | string },
    TGridColumnIdentity,
    TViewModel extends ServiceViewModel<TCollectionItem, TFilterParams, TGridColumnIdentity> = ServiceViewModel<TCollectionItem, TFilterParams, TGridColumnIdentity>,
    TServiceMenuItems extends ServiceMenuItems = ServiceMenuItems> extends ListenersRegistry(class {}) implements OnInit, OnDestroy {
    //#region DECLARATIONS
    protected readonly searchSubject = new Subject<string>();
    public isFormReady = false;
    public viewModel!: TViewModel;

    protected readonly injector = inject(Injector);
    protected logger: ConsoleLogger;
    protected cacheData!: { viewKind: string; page: number; search: string; };
    protected localContext!: LocalContext;
    protected abstract states: ServiceStates;
    protected abstract cacheKey: string;
    private paramsChangeSubscription!: Subscription;

    // state parms
    protected lastTouchedId!: number;
    protected lastTouchedKind!: string;
    protected navigationExtras?: NavigationExtras;

    private readonly platform = inject(Platform);
    private readonly searchViewSettingsRepository = inject(ViewSettingsRepository) as unknown as ViewSettingsRepositoryBase;

    static readonly SEARCH_DEBOUNCE_TIME = 500;

    public constructor(
        protected readonly rootContainerOverlayService: RootContainerOverlayService,
        protected readonly serviceViewCache: ServiceViewCache,
        protected readonly rootContext: RootContextBase,
        protected readonly dialogService: DialogInsService,
        protected readonly toastService: ToastService,
        protected readonly l10n: L10nBase,
        protected readonly routingService: RoutingService,
        protected readonly menuService: MenuService
    ) {
        super();
        this.logger = new ConsoleLogger(this.constructor.name);
        this.localContext = rootContext.createLocalContext();
        this.navigationExtras = routingService.getExtrasFromPreviousState() ?? undefined;
    }
    //#endregion

    //#region COMPONENT EVENTS
    public async ngOnInit(): Promise<void> {
        try {
            await this.preloadDataAsync();
            await this.prepareComponentDataAsync();
            this.isFormReady = true;

            this.searchSubject.pipe(
                debounceTime(ServiceBase.SEARCH_DEBOUNCE_TIME),
                distinctUntilChanged()
            ).subscribe(() => {
                this.onSearchAsync();
            });
        } catch (err) {
            this.logger.error(err);
        }
    }

    public ngAfterViewInit(): void {
        this.paramsChangeSubscription = this.routingService.currentStateParams$.subscribe(async data => {
            const viewKindChanged = data.curr?.['viewKind'] !== data.prev?.['viewKind'];
            if (viewKindChanged) {
                this.viewModel.search = '';
            }
            this.updateCache();

            if (!viewKindChanged) return;
            this.isFormReady = false;
            await this.prepareComponentDataAsync();
            this.isFormReady = true;
        });
    }

    protected async prepareComponentDataAsync(): Promise<void> {
        this.rootContainerOverlayService.show();
        await this.prepareViewModelAsync();
        this.loadParams();
        await this.loadFilterOptionsAsync();
        await this.loadGridOptionsAsync();
        this.updateUrl();

        await this.getDataAsync();
        if (this.viewModel.gridDataSource$.value.length) {
            this.viewModel.gridOptions.selection?.clear();
            this.viewModel.gridOptions.selection?.select(this.viewModel.gridDataSource$.value[0]);
        }
    }

    public ngOnDestroy(): void {
        this.paramsChangeSubscription?.unsubscribe();
        this.deregisterAllListeners();
        this.localContext.dispose();
    }

    //#endregion

    //#region VIEW MODEL
    protected abstract preloadDataAsync(): Promise<void>;

    protected async prepareViewModelAsync(): Promise<void> {
        let searchScope = await this.searchViewSettingsRepository.getViewSettingsAsync<SearchScope>('view_search-scope') ?? 'all';
        const onSearchScopeChange = () => this.onSearchScopeChangeAsync().catch(err => this.logger.error(err));
        this.viewModel = <TViewModel><unknown>{
            // search
            search: null,
            searchActionAsync: this.onSearchAsync.bind(this),
            searchActive: false,
            get searchScope(): SearchScope {
                return searchScope;
            },
            set searchScope(scope: SearchScope) {
                if (scope === searchScope) return;
                searchScope = scope;
                void onSearchScopeChange();
            },
            searchActiveTriggered: (val: boolean) => {
                if (this.viewModel.gridOptions.api?.gridControl) {
                    this.viewModel.gridOptions.api.gridControl.preventAutoFocus = val;
                }
            },
            isFiltered: this.isFiltered.bind(this),

            // pagination
            currentPage: null,
            totalAmount: 0,
            paginationOptions: {
                limit: 25,
                range: 5
            },
            getPageUrl: this.getPageUrl.bind(this),

            // kind
            kind: null,

            // grid
            gridOptions: {
                selection: new SelectionModel<TCollectionItem>(this.isSelectionMultiple, []),
                contextMenu: this.gridRowContextMenu,
                api: {
                    onSortingChange: (event: GridSortingEvent<unknown>) => {
                        this.refreshDataWithSelectionRefreshAsync('first-row');
                        this.onGridSortingChange(event);
                    },
                    onColumnResized: (event: GridResizedEvent<TCollectionItem>, identityName: TGridColumnIdentity) => {
                        this.onGridColumnResizeChange({event, identityName});
                    },
                    onDisplayedColumnChange: (columns: Map<TGridColumnIdentity, boolean>) => {
                        this.onGridDisplayedColumnChange(columns);
                    },
                    onGridDefaultTriggered: () => {
                        this.refreshDataWithSelectionRefreshAsync('first-row');
                        this.onGridDefaultTriggered();
                    },
                    onSummaryChanged: () => {
                        this.onGridSummaryChanged();
                    },
                    getInitialEntity: () => {
                        return this.viewModel.gridDataSource$.getValue().find(elem => +elem.id === +this.lastTouchedId);
                    },
                    keyboardControl: {
                        onDelete: (_: KeyboardEvent) => {
                            this.onDeleteAsync(this.viewModel.gridOptions.selection?.selected[0]);
                        }
                    },
                    mouseControl: {
                        rowDblClick: this.rowDblClick.bind(this),
                    }
                }
            },
            gridDataSource$: new BehaviorSubject([]),
            getSelectedEntitiesCount: () => this.viewModel.gridOptions.selection?.selected.length,

            // actions
            getAddRouteObject: this.getAddRouteObject.bind(this),
            getEditRouteObject: this.getEditRouteObject.bind(this),

            filterParams: {}
        };
    }
    //#endregion

    //#region VIEW MODEL ACTIONS
    protected resetCurrentPage(): void {
        this.viewModel.currentPage = 1;
    }

    public async onPageChange(page: number): Promise<void> {
        this.viewModel.currentPage = page;
        this.rootContainerOverlayService.show();
        this.updateUrl();
        await this.refreshDataWithSelectionRefreshAsync('first-row');
    }

    protected async onSearchAsync(): Promise<void> {
        this.resetCurrentPage();
        this.updateUrl();
        await this.refreshDataWithSelectionRefreshAsync('clear');
    }

    protected async onSearchScopeChangeAsync(): Promise<void> {
        await this.searchViewSettingsRepository.saveViewSettingsAsync('view_search-scope', this.viewModel.searchScope);
        if (!this.viewModel.search) return;
        this.resetCurrentPage();
        this.updateUrl();
        await this.refreshDataWithSelectionRefreshAsync('clear');
    }

    public onSearchChange(searchTerm: string): void {
        this.viewModel.search = searchTerm;
        this.searchSubject.next(searchTerm);
    }

    public toggleColumn(column: any, isChecked: boolean): void {
        const currentColumns = this.viewModel.gridColumns$.getValue();
        const columnsMap = new Map<TGridColumnIdentity, boolean>();

        currentColumns.forEach(c => columnsMap.set(c.identityName, c.identityName === column.identityName ? isChecked : !c.isHidden));

        this.onGridDisplayedColumnChange(columnsMap);

        column.isHidden = !isChecked;
        (this.viewModel.gridColumns$ as BehaviorSubject<any>).next(currentColumns);
    }

    public restoreDefaults(): void {
        this.onGridDefaultTriggered();
    }

    protected get firstOfSelectedEntities(): TCollectionItem | null {
        const selectedEntities: TCollectionItem[] | undefined = this.viewModel.gridOptions.selection?.selected;
        if (!selectedEntities || selectedEntities.length !== 1) return null;
        return selectedEntities[0];
    }

    protected getMultiSelectionActionEntities(actionEntity: TCollectionItem): TCollectionItem[] {
        return this.isCompactView ? [actionEntity] : (this.viewModel.gridOptions.selection?.selected || []);
    }

    protected get isSingleEntitySelected(): boolean {
        return (this.viewModel.gridOptions.selection?.selected.length ?? 0) === 1;
    }

    protected get isMoreThanOneEntitySelected(): boolean {
        return (this.viewModel.gridOptions.selection?.selected.length ?? 0) > 1;
    }

    protected get isNoneEntitySelected(): boolean {
        return !this.isSingleEntitySelected && !this.isMoreThanOneEntitySelected;
    }

    public handleGridRowMoreButtonClick(event: PointerEvent, entity: TCollectionItem): void {
        this.menuService.createMenu(this.gridRowMoreButtonMenu.items, event, { context: entity });
    }

    protected async onFilterChangeAsync(filterValue: TFilterParams): Promise<void> {
        this.resetCurrentPage();
        await this.onFilterAsync(filterValue);
    }

    protected isFiltered(): boolean {
        if (!this.viewModel) return false;
        return Boolean(this.viewModel.search) || this.filtersApplied;
    }

    protected get filtersApplied(): boolean {
        if (!this.viewModel) return false;
        for (const key in this.viewModel.filterParams) {
            if (this.viewModel.filterParams[key] != null) {
                return true;
            }
        }
        return false;
    }

    protected async refreshDataAsync(): Promise<void> {
        const currentIndexes = this.viewModel.gridOptions.api?.gridControl?.selection?.selected()?.map(elem => this.viewModel.gridDataSource$.value.findIndex(gridItem => gridItem === elem));

        await this.getDataAsync();

        this.selectEntity('same-indexes', currentIndexes);
    }

    protected async refreshDataWithSelectionRefreshAsync(mode: Exclude<SelectionEntityMode, 'same-indexes'>): Promise<void> {
        await this.getDataAsync();

        if ((this.viewModel.gridDataSource$.value.length === 0) || mode === 'clear') {
            this.viewModel.gridOptions.api?.gridControl?.selection?.clear();
            return;
        }

        this.selectEntity(mode);
    }

    protected selectEntity(mode: Exclude<SelectionEntityMode, 'clear'>, previousIndexes?: number[]): void {
        if (this.isCompactView) return;

        if (mode === 'same-indexes' && previousIndexes) {
            if (previousIndexes.length === 1) {
                const currentItem = this.viewModel.gridDataSource$.value[previousIndexes[0]];
                currentItem
                    ? this.viewModel.gridOptions.api?.gridControl?.selection?.single(currentItem)
                    : this.selectEntity('sibling-row');
            } else {
                this.viewModel.gridOptions.api?.gridControl?.selection?.multi(this.viewModel.gridDataSource$.value.filter((_, index) => previousIndexes.includes(index)));
            }
        } else if (mode === 'sibling-row') {
            this.viewModel.gridOptions.api?.gridControl?.selection?.sibling();
        } else {
            this.viewModel.gridOptions.api?.gridControl?.selection?.single(this.viewModel.gridDataSource$.value[0]);
        }
    }

    //#endregion

    //#region FILTER
    protected abstract loadFilterOptionsAsync(): Promise<void>;

    protected abstract loadGridOptionsAsync(): Promise<void>;

    protected async onFilterAsync(params: TFilterParams): Promise<void> {
        try {
            this.rootContainerOverlayService.show();
            this.viewModel.filterParams = params;
            this.updateUrl();
            await this.refreshDataWithSelectionRefreshAsync('first-row');
        } catch (err) {
            this.logger.error(err);
        }
    }

    protected get paramsToWriteInUrl(): Dictionary<unknown> {
        const params: Dictionary<unknown> = {
            page: this.viewModel.currentPage,
            search: this.viewModel.search,
            viewKind: this.viewModel.kind.viewKind
        };
        const serializedFilterParams = this.serializeFilterParams();
        for (const x in serializedFilterParams) {
            params[x] = (this.isFilterAvailable() && serializedFilterParams[x] != null) ? serializedFilterParams[x] : null;
        }

        return params;
    }

    private serializeFilterParams(): TFilterParams {
        return SerializationUtils.serialize(this.viewModel.filterParams, { dateFormat: DataFormats.date.webApi });
    }

    protected getDefaultFilterParametersOnSearchActive(): TFilterParams {
        return null as unknown as TFilterParams;
    }

    private isFilterAvailable(): boolean {
        return !this.viewModel.search || this.isSearchScopeFiltered();
    }

    private isSearchScopeFiltered(): boolean {
        return this.viewModel.searchScope === 'filtered' && this.filtersApplied;
    }

    //#endregion

    //#region URL MANAGEMENT

    private updateUrlTimeoutHandler!: ReturnType<typeof setTimeout>;
    private updateUrl(): void {
        clearTimeout(this.updateUrlTimeoutHandler);
        this.updateUrlTimeoutHandler = setTimeout(() => {
            this.routingService.setQueryParamsAsync(this.paramsToWriteInUrl, undefined);
        }, 200);
    }

    protected getAddRouteObject(params?: Object): NavigationRoute {
        return {
            url: this.states.new ?? '',
            params
        };
    }

    protected getEditRouteObject(entity?: TCollectionItem): NavigationRoute {
        if (!entity) {
            const selected = this.firstOfSelectedEntities;
            if (!selected) return { url: '', params: undefined };
            entity = selected;
        }
        return {
            url: this.states.edit ?? '',
            params: { id: entity.id }
        };
    }

    public get isEditButtonVisible(): boolean {
        return this.viewModel.gridOptions.selection?.selected.length === 1;
    }

    protected getPageUrl(page: number): string {
        return this.routingService.href([this.states.service], {queryParams: {page}});
    }

    //#endregion

    //#region QUERY PARAMS
    protected loadParams(): void {
        const stateParams = this.routingService.currentState.params || {};
        this.cacheData = this.serviceViewCache.cache.get(this.cacheKey) || { viewKind: '', page: 1, search: '' };
        //#region state params

        // KIND
        if (stateParams['viewKind']) {
            this.viewModel.kind = this.getTypes().find(t => !t.default && t['viewKind'] == stateParams['viewKind'])!;
            if (!this.viewModel.kind) {
                this.viewModel.kind = this.getTypes().find(t => t.default)!;
            }
        }

        // PAGE
        if (stateParams['page'] != null) {
            this.viewModel.currentPage = Number(stateParams['page']);
        }

        // SEARCH
        if (stateParams['search']) {
            this.viewModel['search'] = stateParams['search'];
        }

        // TOUCHED
        if (this.routingService.currentState.params?.['lastTouchedId']) {
            this['lastTouchedId'] = Number(this.routingService.currentState.params['lastTouchedId']);
        }

        if (this.routingService.currentState.params?.['lastTouchedKind']) {
            if (!this.viewModel.kind)
                this.viewModel.kind = this.getTypes().find(type => !type.default && type.viewKind === this.routingService.currentState?.params?.['lastTouchedKind'])!;
        }

        //#endregion

        //#region cache
        if (!this.routingService.previousState?.url) {
            // KIND
            if (!this.viewModel.kind && this.cacheData.viewKind) {
                this.viewModel.kind = this.getTypes().find(t => !t.default && t.viewKind === this.cacheData.viewKind) || this.getTypes().find(t => t.default) || this.getTypes()[0];
            }

            // PAGE
            if (!this.viewModel.currentPage && this.cacheData.page != null) {
                this.viewModel.currentPage = this.cacheData.page;
            }

            // SEARCH
            if (this.viewModel.search == null && this.cacheData.search != null && this.cacheData.search !== '') {
                this.viewModel.search = this.cacheData.search;
            }
        }

        //#endregion

        //#region defaults

        // KIND
        if (!this.viewModel.kind) {
            this.viewModel.kind = this.getTypes().find(t => t.default) || this.getTypes()[0];
        }

        // PAGE
        if (!this.viewModel.currentPage) {
            this.viewModel.currentPage = 1;
        }

        // SEARCH
        if (!this.viewModel.search) {
            this.viewModel.search = '';
        }

        //#endregion
    }

    private updateCache(): void {
        this.serviceViewCache.cache.put(this.cacheKey, {
            search: this.viewModel.search,
            page: this.viewModel.currentPage,
            viewKind: this.viewModel.kind.viewKind as unknown as string
        });
    }

    protected get queryParameters(): PagedCollectionQueryParams {
        const sorting = this.viewModel.gridOptions?.sorting;
        const sortingBy = sorting?.key || '';
        const direction = sorting?.direction === OrderingDirection.Asc ? OrderDirection.Asc : OrderDirection.Desc;
        const pageSize = this.viewModel.paginationOptions?.limit ?? 0;
        const pageNo = this.viewModel.currentPage - 1;
        const search = encodeURIComponent(this.viewModel.search);

        return {
            pageNo,
            pageSize,
            search,
            orderBy: [`${sortingBy}+${direction}`],
        };
    }

    protected abstract getTypes(): ServiceKindItem[];
    //#endregion

    //#region DATA
    protected async getDataAsync(): Promise<void> {
        const filterParams = this.isFilterAvailable() ? this.serializeFilterParams() : this.getDefaultFilterParametersOnSearchActive();
        try {
            let items = [];
            let totalItemsCount = 0;

            this.rootContainerOverlayService.show();
            ({ items, totalItemsCount } = await this.getCollectionAsync(filterParams));

            if (items.length === 0 && totalItemsCount) {
                const lastPage = Math.ceil(totalItemsCount / this.viewModel.paginationOptions.limit);
                this.viewModel.currentPage = lastPage;
                this.updateUrl();
                ({ items, totalItemsCount } = await this.getCollectionAsync(filterParams));
            }

            this.viewModel.totalAmount = totalItemsCount;
            this.viewModel.gridDataSource$.next(items);
        } catch (err) {
            this.logger.error(err);
        } finally {
            this.rootContainerOverlayService.hide();
        }
    }

    protected onItemsRemoved(): void {
        if (!this.viewModel.gridDataSource$) return;

        if (this.viewModel.gridDataSource$.value.length === 1 && this.viewModel.currentPage > 1) {
            this.viewModel.currentPage -= 1;
            this.updateUrl();
        }
    }

    protected abstract onGridSortingChange(event: GridSortingEvent<unknown>): void;
    protected abstract onGridColumnResizeChange(data: {event: GridResizedEvent<TCollectionItem>, identityName: TGridColumnIdentity}): void;
    protected abstract onGridDisplayedColumnChange(columns: Map<TGridColumnIdentity, boolean>): void;
    protected abstract onGridDefaultTriggered(): void;
    protected abstract getCollectionAsync(params?: TFilterParams): Promise<PagedCollectionQueryResult<TCollectionItem>>;
    protected onGridSummaryChanged(): void {}
    //#endregion

    public get sortData(): SortDataDialogComponentData {
        const sortingColumns = this.viewModel.gridColumns$.value.filter(elem => elem.sortingBy);
        const sorting = this.viewModel.gridOptions.sorting || { direction: OrderingDirection.Asc, key: '' as any };
        const selectedColumn = sortingColumns.find(elem => elem.sortingBy === sorting.key)?.identityName;

        return {
            direction: sorting.direction!,
            items: sortingColumns.map(elem => ({label: elem.label ?? elem.header ?? '', value: elem.identityName as unknown})),
            currentValue: selectedColumn,
            onChange: sorting => {
                const sortingBy = sortingColumns.find(elem => elem.identityName === sorting.value)?.sortingBy;

                this.onGridSortingChange({direction: sorting.direction, key: sorting.value as any});
                this.viewModel.gridOptions.sorting = {direction: sorting.direction, key: sortingBy as any};
                this.refreshDataWithSelectionRefreshAsync('clear');
            }
        };
    }

    public get filterData(): FilterDataDialogComponentData {
        return {
            filterOptions: this.viewModel.filterChipsOptions,
            isFiltered: () => this.filtersApplied
        };
    }

    public get isCompactView(): boolean {
        return this.platform.ANDROID || this.platform.IOS;
    }

    //#region MENU

    protected getMenuItems(): TServiceMenuItems {
        return <TServiceMenuItems>{
            add: new MenuItem({
                label: this.l10n._T('ui.menu.add'),
                icon: 'icon-plus',
            }),
            edit: new MenuItem({
                label: this.l10n._T('ui.menu.edit'),
                icon: 'icon-edit',
                disabled: () => !this.isSingleEntitySelected,
                click: (entity?: TCollectionItem) => {
                    if (entity) this.onEdit(entity);
                }
            }),
            separator: new MenuItemSeparator(),
            duplicate: new MenuItem({
                label: this.l10n._T('ui.menu.duplicate'),
                icon: 'icon-duplicate',
                disabled: () => !this.isSingleEntitySelected,
                click: (entity?: TCollectionItem) => {
                    if (entity) this.onReplicate(entity);
                }
            }),
            remove: new MenuItem({
                label: this.l10n._T('ui.menu.remove'),
                icon: 'icon-remove',
                disabled: () => !this.isSingleEntitySelected,
                click: (entity?: TCollectionItem) => {
                    if (entity) this.onDeleteAsync(entity);
                }
            })
        };
    }

    public abstract get contextBarMenu(): Menu;
    protected abstract get gridRowContextMenu(): Menu;
    protected abstract get gridRowMoreButtonMenu(): Menu;

    //#endregion

    //#region MENU ACTIONS

    protected onEdit(entity: TCollectionItem): void {
        if (!entity) {
            const selected = this.firstOfSelectedEntities;
            if (!selected) return;
            entity = selected;
        }
        this.routingService.navigate([this.states.edit], {queryParams: { id: entity.id }});
    }

    protected onAdd(kind?: string): void {
        this.routingService.navigate([this.states.new], {queryParams: { kind: kind || this.viewModel.kind.viewKind }});
    }

    protected onReplicate(entity: TCollectionItem): void {
        if (!entity) {
            const selected = this.firstOfSelectedEntities;
            if (!selected) return;
            entity = selected;
        }
        this.routingService.navigate([this.states.new], {queryParams: { replicateId: entity.id }});
    }

    protected rowDblClick(_event: PointerEvent, entity: TCollectionItem): void {
        const { url, params: queryParams } = this.getEditRouteObject(entity);
        if (!url) return;

        this.routingService.navigate([url], { queryParams });
    }

    protected async onDeleteAsync(entity?: TCollectionItem): Promise<void> {
        try {
            if (!entity) {
                const selected = this.firstOfSelectedEntities;
                if (!selected) return;
                entity = selected;
            }
            const dialogMessage = await this.getDeleteDialogMessageAsync(entity);

            const result = await this.dialogService.showConfirmAsync({
                title: this.l10n._T('scenes.shared.service.dialog.removing.title'),
                text: dialogMessage,
                buttons: [{
                    type: 'accent',
                    text: this.l10n._T('scenes.shared.service.dialog.removing.buttons.yes'),
                    state: DialogState.Confirm
                }, {
                    text: this.l10n._T('scenes.shared.service.dialog.removing.buttons.no'),
                    state: DialogState.Cancel,
                    focus: true
                }]
            });
            if (result.state === DialogState.Confirm) {
                this.rootContainerOverlayService.show();

                const deleteResponse = await this.deleteEntityAsync(entity);
                if (deleteResponse) {
                    await this.handleDeleteEntityResponse(deleteResponse);
                }

                this.rootContainerOverlayService.hide();
            } else {
                this.selectEntity('same-indexes', [this.viewModel.gridDataSource$.value.findIndex(gridItem => gridItem === this.firstOfSelectedEntities)]);
            }

        } catch(err) {
            this.logger.error(err);
        }
    }

    protected async handleDeleteEntityResponse(deleteResponse: DeleteResponse): Promise<void> {
        if (typeof deleteResponse !== 'undefined') {
            if (deleteResponse.responseCode === RepositoryResponseCode.DataNotFound) return;

            this.toastService.showAsync({
                text: this.getDeleteToastMessage(deleteResponse),
                type: deleteResponse.deletedSuccessfully ? 'success' : 'error',
                duration: (deleteResponse.deletedSuccessfully || deleteResponse.errorCode) ? ToastDelay.Regular : undefined,
            });
            if (deleteResponse.deletedSuccessfully) {
                this.onItemsRemoved();
                await this.refreshDataWithSelectionRefreshAsync('sibling-row');
            }
        }
    }

    protected abstract getDeleteToastMessage(deleteResponse: DeleteResponse): string;
    protected abstract getDeleteDialogMessageAsync(entity: TCollectionItem): Promise<string>;
    protected abstract deleteEntityAsync(entity: TCollectionItem): Promise<DeleteResponse | undefined>;
    protected get isSelectionMultiple(): boolean {
        return false;
    }

    //#endregion

}
